// ─────────────────────────────────────────────────────────────────────────────
// THE MEMBER DOOR (Ticket 9) — client for the show's member service.
//
// The member service is the co-located group AppView: it resolves EFFECTIVE
// membership server-side (two-sided group model) and serves member card
// bodies only to active members. This client is the show space's side of that
// contract.
//
// THE FENCE, extended honestly. The show space's privacy architecture is
// origin-typed:
//
//   show origin      bare anonymous GETs only            (showFence.ts)
//   runtime origin   identity + preference data          (the fan's own agent)
//   member service   identity, NEVER preference data     (THIS module)
//
// Membership is an identified relationship — a join carries who is joining,
// and a member body fetch must prove who is asking. What the member service
// must structurally never receive is the fan's PREFERENCE data: interests,
// topics, mutes, rules, rankings, why-strings. `assertMemberDoorRequest`
// enforces that on every call, the same tripwire style as showFence.
// ─────────────────────────────────────────────────────────────────────────────

import {MEMBER_SERVICE_URL} from './config'
import type {Card} from './candidateSet'

export class MemberDoorViolation extends Error {
  constructor(message: string) {
    super(`member-door fence violation: ${message}`)
    this.name = 'MemberDoorViolation'
  }
}

export class MemberDoorError extends Error {
  status: number
  door: {card?: Card; membershipGroup?: string} | null
  constructor(message: string, status: number, door: MemberDoorError['door'] = null) {
    super(message)
    this.name = 'MemberDoorError'
    this.status = status
    this.door = door
  }
}

/** Preference-shaped keys that must never travel to the member service. */
const PREFERENCE_KEYS = new Set([
  'interests',
  'profile',
  'topics',
  'authors',
  'mutes',
  'rules',
  'weights',
  'weight',
  'ranking',
  'order',
  'why',
  'score',
  'scores',
])

/** Recursively scan a JSON-able payload for preference-shaped keys. */
function findPreferenceKey(value: unknown, path = ''): string | null {
  if (!value || typeof value !== 'object') return null
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const p = path ? `${path}.${k}` : k
    if (PREFERENCE_KEYS.has(k.toLowerCase())) return p
    const nested = findPreferenceKey(v, p)
    if (nested) return nested
  }
  return null
}

/**
 * Assert a member-service request is (a) addressed to the member service and
 * (b) free of preference data — body, query string, everything.
 * `baseUrl` defaults to the build's configured member service (parameterized
 * so the fence itself is testable without a configured build).
 */
export function assertMemberDoorRequest(url: string, init?: RequestInit, baseUrl: string = MEMBER_SERVICE_URL): void {
  if (!baseUrl) {
    throw new MemberDoorViolation('member service is not configured; no member-door request may be made')
  }
  const target = new URL(url, baseUrl).origin
  const expected = new URL(baseUrl).origin
  if (target !== expected) {
    throw new MemberDoorViolation(`request addressed to ${target}, expected the member service ${expected}`)
  }
  const parsed = new URL(url, baseUrl)
  for (const [k] of parsed.searchParams) {
    if (PREFERENCE_KEYS.has(k.toLowerCase())) {
      throw new MemberDoorViolation(`query parameter "${k}" is preference data; the member service must never receive it`)
    }
  }
  if (init?.body != null) {
    let payload: unknown
    try {
      payload = JSON.parse(String(init.body))
    } catch {
      throw new MemberDoorViolation('member-service bodies must be JSON (auditable)')
    }
    const hit = findPreferenceKey(payload)
    if (hit) {
      throw new MemberDoorViolation(`body field "${hit}" is preference data; the member service must never receive it`)
    }
  }
  if (init?.credentials === 'include') {
    throw new MemberDoorViolation('member-service requests use an explicit bearer, never ambient cookies')
  }
}

/** True when the member service is deployed/configured for this build. */
export function memberDoorConfigured(): boolean {
  return Boolean(MEMBER_SERVICE_URL)
}

async function callXrpc(
  method: string,
  params: Record<string, string>,
  token: string | null,
  init: {post?: boolean; body?: unknown} = {},
): Promise<{status: number; body: any}> {
  const qs = new URLSearchParams(params).toString()
  const url = `${MEMBER_SERVICE_URL}/xrpc/${method}${qs ? `?${qs}` : ''}`
  const reqInit: RequestInit = {
    method: init.post ? 'POST' : 'GET',
    credentials: 'omit',
    headers: {
      ...(token ? {authorization: `Bearer ${token}`} : {}),
      ...(init.post ? {'content-type': 'application/json'} : {}),
    },
    ...(init.post && init.body !== undefined ? {body: JSON.stringify(init.body)} : {}),
  }
  assertMemberDoorRequest(url, reqInit)
  const res = await fetch(url, reqInit)
  let body: any = null
  try {
    body = await res.json()
  } catch {
    body = null
  }
  return {status: res.status, body}
}

/**
 * Fetch a member card's body. 200 → the goods. 403 → MemberDoorError carrying
 * the door (public card + group URI) so the concierge can explain it.
 * The bearer is the fan's existing session token — the service resolves the
 * viewer identity server-side; the client never sends a DID it merely claims.
 */
export async function fetchMemberBody(cardId: string, token: string | null): Promise<{card: Card; body: unknown}> {
  const {status, body} = await callXrpc('com.authorityone.exchange.getCandidateBody', {cardId}, token)
  if (status === 200) return body
  if (status === 403) {
    throw new MemberDoorError(body?.message ?? 'membership required', 403, body?.door ?? null)
  }
  throw new MemberDoorError(body?.message ?? `member service error (${status})`, status)
}

/**
 * Redeem an invite code at the door. The service mints the owner-side invite;
 * activation still needs the fan's own membership record (written by their
 * agent — the two-sided model; the service tells us what's owed in `next`).
 */
export async function joinWithInviteCode(
  groupUri: string,
  code: string,
  token: string | null,
): Promise<{ok: boolean; status: string; next?: string}> {
  const {status, body} = await callXrpc(
    'com.authorityone.group.joinWithInviteCode',
    {},
    token,
    {post: true, body: {group: groupUri, code}},
  )
  if (status === 200) return body
  throw new MemberDoorError(body?.message ?? `join failed (${status})`, status)
}

/** The viewer's membership state for the door badge (getGroup viewer state). */
export async function fetchMemberState(
  groupUri: string,
  token: string | null,
): Promise<{status: string | null; role: string | null; canJoin: boolean}> {
  const {status, body} = await callXrpc('com.authorityone.group.getGroup', {group: groupUri}, token)
  if (status !== 200) return {status: null, role: null, canJoin: false}
  const viewer = body?.group?.viewer ?? {}
  const bare = (v: unknown) => (typeof v === 'string' && v.includes('#') ? v.split('#').pop()! : (v as string | null))
  return {
    status: viewer.status ? bare(viewer.status) : null,
    role: viewer.role ? bare(viewer.role) : null,
    canJoin: Boolean(viewer.canJoin),
  }
}
